import { useState } from 'react';
import styled from 'styled-components';

import { useMutation } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import { CreatableMultiSelect } from 'components/shared/form/CreatableSelect';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal from 'components/shared/StandardModal';
import Tooltip from 'components/shared/Tooltip';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import { ReferralSource } from 'models/ReferralSource';
import { createReferralSource, updateReferralSource } from 'services/api/referralSources';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import InfoIconOutline from 'svg/InfoIconOutline';

export type ReferralSourceModalProps = {
  // Absent means Add; present means Edit, and the form opens on that source's current state.
  referralSource?: ReferralSource;
  clientId?: string;
  onClose: () => void;
};

const ALIAS_HELP = 'Alternative names this referral source might be listed under so referrals are matched correctly.';

export default function ReferralSourceModal({ referralSource, clientId, onClose }: ReferralSourceModalProps) {
  const isEdit = Boolean(referralSource);
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const [name, setName] = useState(referralSource?.name ?? '');
  const [aliases, setAliases] = useState<string[]>(referralSource?.aliases ?? []);
  const [error, setError] = useState<string | null>(null);

  // useMutation directly rather than useMutateWithToast: that hook raises a generic "Something went
  // wrong" toast, and the two refusals this form can hit (a name already in use, a wording that
  // belongs to another source) are things the person can fix — but only if they can read which one
  // happened. It matters more than usual here, because product populates these lists by driving
  // this screen with an agent, which has nothing to go on but the rendered text.
  const { mutate, isPending } = useMutation({
    mutationFn: isEdit ? updateReferralSource : createReferralSource,
    onSuccess: (saved: ReferralSource) => {
      invalidateQueries({ referralSources: true });
      addToast({ text: `${saved.name} saved` });
      onClose();
    },
    onError: (e: any) => {
      setError(e?.response?.data?.message || 'Something went wrong. Please try again.');
    },
  });

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !isPending;

  // Shown only once a name that was there on open has actually been taken off, so the warning
  // appears when it applies rather than sitting on the form the whole time someone adds names.
  const normalized = aliases.map((alias) => alias.trim().toLowerCase());
  const removedAliases = (referralSource?.aliases ?? []).filter(
    (alias) => !normalized.includes(alias.trim().toLowerCase())
  );

  const handleSubmit = () => {
    setError(null);
    // `aliases` always goes, including as an empty array — that is how the last chip is removed.
    mutate({ id: referralSource?.id as string, clientId, name: trimmedName, aliases });
  };

  return (
    <StandardModal title={isEdit ? 'Edit Referral Source' : 'Add New Referral Source'} onCancel={onClose}>
      {!isEdit && (
        <Intro data-testid='referral-source-intro'>
          This will be saved as a new referral source for future referrals.
        </Intro>
      )}

      <InputGroup title='Referral Source'>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder='Referral Source'
          disabled={isPending}
          data-testid='referral-source-name'
        />
      </InputGroup>

      <InputGroup
        title='Also Known As'
        badge={
          <Tooltip text={ALIAS_HELP} position='bottom'>
            <InfoIconOutline width={14} height={14} fill={colors.black75} />
          </Tooltip>
        }>
        <CreatableMultiSelect
          options={[]}
          values={aliases}
          onChange={setAliases}
          placeholder='Referral Source'
          disabled={isPending}
          menuPortalTarget={document.body}
          dataTestId='referral-source-aliases'
        />
        <Hint>Press enter after each name to add</Hint>
      </InputGroup>

      {(removedAliases.length > 0 || !!error) && (
        <BelowFields>
          {/* The same true-but-easy-to-miss consequence the delete modal spells out, and for the
              same reason: a completed referral keeps its referral source because a finished run is
              never re-evaluated, but one still in progress is re-reconciled on its next pass, and a
              wording that no longer matches anything loses the tag and asks again. AC8 said these
              referrals were unaffected, which is only true of the completed ones. */}
          {removedAliases.length > 0 && (
            <Notice data-testid='referral-source-removal-notice'>
              Removing {removedAliases.length === 1 ? 'a name' : 'names'} stops future referrals worded that way from
              matching this source. Referrals you have already completed keep reporting under it. Any referral still in
              progress worded that way will ask for its referral source again.
            </Notice>
          )}
          {error && <ErrorText data-testid='referral-source-error'>{error}</ErrorText>}
        </BelowFields>
      )}

      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={isPending} disabled={!canSubmit} data-testid='referral-source-save'>
          {isEdit ? 'Save' : 'Submit'}
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Intro = styled(BodySmall)`
  display: block;
  margin-bottom: 24px;
`;

const Hint = styled(BodySmall)`
  display: block;
  margin-top: 6px;
  color: ${colors.black75};
`;

/* Holds whatever sits between the last field and the buttons.

   The negative top cancels the empty row InputGroup reserves for its own per-field error
   (ErrorMessageContainer: 6px margin + 20px min-height). Neither message here belongs to one field
   — the refusal can be about the name or about a wording, and the removal notice is about the save
   as a whole — so they render after that group and inherited the reserved gap, leaving them
   floating midway between the fields and the button, attached to neither. The bottom margin is
   needed for the opposite reason: without it the text sat almost against Submit and read as a label
   on the button rather than as something to read before pressing it. */
const BelowFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: -20px 0 24px;
`;

const Notice = styled(BodySmall)`
  display: block;
  color: ${({ theme }) => theme.colors.black75};
`;

const ErrorText = styled(BodySmall)`
  display: block;
  color: ${({ theme }) => theme.colors.red100};
`;
